/**
 * cortex-admin の ownership_transfers API contract（zod schema + 型）。
 *
 * 退職時に対象ユーザーが所有していた pipeline_config 等を admin へ移譲または
 * delete した監査レコードを表す。writer だった backoffice-general-affairs pipeline は
 * 廃止済み (docs/infra/backoffice-teardown.md) で、現在は新規レコードを書く側が居ない。
 * admin.air-closet.ai 上では本 contract を介して移譲済みレコードを一覧表示し、
 * admin が最終的に「pending → kept / deleted」を決着する。
 *
 * @graph-stack cortex-admin
 * @graph-domain HR
 * @graph-business 退職時の cortex リソース移譲監査レコードを admin UI に公開する contract。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * ownership_transfers レコードの対象コレクション種別。
 *
 * 既存レコードは writer だった backoffice-general-affairs が **単数形** で保存していた
 * ため、ここも単数形で揃える。複数形にすると実レコードが全件
 * `OwnershipTransferRecordSchema.parse()` で落ちて list/get/markKept が壊れる。
 * @graph-connects none
 */
export const OwnershipTransferResourceTypeSchema = z.enum([
    'pipeline_config',
    'pipeline_api_key',
    'db_credential',
    'mcp_user_auth',
]);
/** 自動移譲時のアクション。 @graph-connects none */
export const OwnershipTransferActionSchema = z.enum(['transfer', 'delete']);
/** Phase 2 (admin UI) で決着したかのステータス。 @graph-connects none */
export const OwnershipTransferReviewStatusSchema = z.enum([
    'pending',
    'kept',
    'reassigned',
    'deleted',
]);
/** ownership_transfers レコード。 @graph-connects none */
export const OwnershipTransferRecordSchema = z.object({
    id: z.string().min(1),
    originalOwnerEmail: z.email(),
    originalOwnerNickname: z.string().nullable(),
    newOwnerEmail: z.email().nullable(),
    resourceType: OwnershipTransferResourceTypeSchema,
    resourceId: z.string().min(1),
    resourceSnapshot: z.record(z.string(), z.unknown()),
    transferredAction: OwnershipTransferActionSchema,
    transferredAt: z.string(),
    transferredBy: z.string().min(1),
    reason: z.enum(['offboarding', 'manual_review']),
    reviewStatus: OwnershipTransferReviewStatusSchema,
    reviewedAt: z.string().nullable(),
    reviewedBy: z.string().nullable(),
});
/**
 * ownership_transfers 一覧フィルタ。
 * 指定がない場合は全件 (admin 用) 返す。`originalOwnerEmail` を指定すると同じ退職者の
 * リソースをまとめて出す。`reviewStatus` で pending のみ・kept のみといった絞り込みを行う。
 * @graph-connects none
 */
export const OwnershipTransferListFilterSchema = z.object({
    originalOwnerEmail: z.email().optional(),
    reviewStatus: OwnershipTransferReviewStatusSchema.optional(),
    resourceType: OwnershipTransferResourceTypeSchema.optional(),
});
/** ownership_transfers 一覧レスポンス。 @graph-connects none */
export const OwnershipTransferListResponseSchema = z.object({
    records: z.array(OwnershipTransferRecordSchema),
});
/** ownership_transfers 詳細リクエスト。 @graph-connects none */
export const OwnershipTransferIdInputSchema = z.object({
    id: z.string().min(1),
});
/**
 * reviewStatus を `kept` へ更新するリクエスト。
 * 「admin 所有のまま残す」確定操作で、元 doc には触らない。
 * @graph-connects none
 */
export const MarkOwnershipTransferKeptRequestSchema = z.object({
    id: z.string().min(1),
});
/**
 * reviewStatus を `reassigned` へ更新するリクエスト。
 * 元 doc の `createdBy` を `newOwnerEmail` に書き換え、別の owner に再移譲する。
 * `pipeline_config` のみ対応 (executor が単純な `createdBy` 書換で移譲したため、
 * UI からも同じ field を更新するだけで完結する)。
 * `mcp_user_auth` / `pipeline_api_key` / `db_credential` は secret を含む or 再構築が複雑で
 * UI 1 操作では安全に再移譲できないため reject する。
 * @graph-connects none
 */
export const MarkOwnershipTransferReassignedRequestSchema = z.object({
    id: z.string().min(1),
    newOwnerEmail: z.email(),
});
/**
 * reviewStatus を `deleted` へ更新するリクエスト。
 * `transferredAction='transfer'` だった record は元 Firestore doc を実際に削除し、
 * `transferredAction='delete'` だった record (executor が既に削除済み) は監査ステータス
 * だけを更新する。
 * @graph-connects none
 */
export const MarkOwnershipTransferDeletedRequestSchema = z.object({
    id: z.string().min(1),
});
//# sourceMappingURL=ownership-transfers.js.map